import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Button from '../../../shared/components/Button';
import { Input } from '../../../shared/components/Input';
import { Toggle } from '../../../shared/components/Toggle';
import { Modal } from '../../../shared/components/Modal';
import { useAuthStore } from '../../auth/store';
import { useToast } from '../../../shared/components/toast/useToast';
import {
  useConnectInstagram,
  useDisconnectInstagram,
  useMyInstagramStatus,
  useUpdateInstagramSettings,
} from '../hooks/useInstagram';
import { InstagramFetchHealth, InstagramStatusPill } from '../components/InstagramConnectionFacts';
import { formatInstagramDate } from '../format';
import {
  InstagramConnectionStatus,
  REFRESH_CADENCE_MAX_MINUTES,
  REFRESH_CADENCE_MIN_MINUTES,
} from '../types';

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-card rounded-2xl border border-border p-6">
      <h2 className="text-[16px] font-semibold text-foreground">{title}</h2>
      {description && <p className="text-[13px] text-muted-foreground mt-0.5 mb-4">{description}</p>}
      <div className={description ? '' : 'mt-4'}>{children}</div>
    </div>
  );
}

/**
 * Merchant Instagram connection (PE-2178 tier 1, backend PE-2177).
 *
 * MERCHANT-SCOPED ONLY, and not by choice: watchman's `me/instagram/*` routes resolve the
 * merchant as `user.merchantId ?? user.tenantId` FROM THE JWT — a COLLECTIVE session carries
 * neither, so these routes cannot work for TCS staff however the merchant is pinned. Staff
 * get the oversight list at /instagram/connections and a read-only card on merchant detail
 * instead. Same shape as the returns-policy page, for the same reason.
 *
 * The route path is load-bearing: osprey-api's OAuth callback redirects the browser to
 * `/settings/instagram?instagram=connected|error` after Meta comes back, so this page must
 * live exactly there and read that query param.
 *
 * ⚠️ Connect cannot succeed until TCS configures the shared Meta app credential, which is
 * itself blocked on a watchman MERCHANT.MANAGE grant (see instagramApi.ts). The empty state
 * says so rather than letting the merchant hit an opaque 503.
 */
export const InstagramSettingsPage = () => {
  const roleScope = useAuthStore((s) => s.user?.roleScope);
  const isMerchantScoped = roleScope === 'MERCHANT';

  const statusQuery = useMyInstagramStatus(isMerchantScoped);
  const connect = useConnectInstagram();
  const disconnect = useDisconnectInstagram();
  const updateSettings = useUpdateInstagramSettings();

  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();

  const [feedEnabled, setFeedEnabled] = useState(false);
  const [cadence, setCadence] = useState('');
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const connection = statusQuery.data;

  useEffect(() => {
    if (!connection) return;
    setFeedEnabled(connection.feedEnabled);
    setCadence(String(connection.refreshCadenceMinutes));
  }, [connection]);

  // The OAuth round-trip lands back here with ?instagram=connected|error. Report it once,
  // then strip the param so a refresh doesn't replay the toast.
  const oauthResult = searchParams.get('instagram');
  useEffect(() => {
    if (!oauthResult) return;
    if (oauthResult === 'connected') toast.success('Instagram connected');
    else toast.error('Instagram could not be connected. Please try again.');
    const next = new URLSearchParams(searchParams);
    next.delete('instagram');
    setSearchParams(next, { replace: true });
  }, [oauthResult, searchParams, setSearchParams, toast]);

  const cadenceNumber = Number(cadence);
  const cadenceValid =
    Number.isInteger(cadenceNumber) &&
    cadenceNumber >= REFRESH_CADENCE_MIN_MINUTES &&
    cadenceNumber <= REFRESH_CADENCE_MAX_MINUTES;
  const settingsDirty =
    !!connection &&
    (feedEnabled !== connection.feedEnabled || cadenceNumber !== connection.refreshCadenceMinutes);

  const saveSettings = () => {
    if (!cadenceValid) return;
    updateSettings.mutate({ feedEnabled, refreshCadenceMinutes: cadenceNumber });
  };

  const Heading = (
    <div className="mb-5">
      <h1 className="text-[22px] font-semibold text-foreground">Instagram Feed</h1>
      <p className="text-[13px] text-muted-foreground mt-1">
        Connect your Instagram account to show your latest posts on your storefront. The section
        is hidden automatically whenever there is nothing good to show.
      </p>
    </div>
  );

  if (!isMerchantScoped) {
    return (
      <div className="py-6 max-w-4xl mx-auto px-2">
        {Heading}
        <div className="bg-card rounded-2xl border border-border p-6 text-[14px] text-muted-foreground">
          An Instagram connection belongs to a single merchant and is authorised by that merchant
          — the sign-in happens on Instagram, under their account. Open a merchant from{' '}
          <span className="text-foreground font-medium">Merchants</span> to see theirs, or use{' '}
          <span className="text-foreground font-medium">Instagram Connections</span> for the
          platform-wide list.
        </div>
      </div>
    );
  }

  if (statusQuery.isLoading) {
    return (
      <div className="py-6 max-w-4xl mx-auto px-2">
        {Heading}
        <div className="bg-card rounded-2xl border border-border p-6 text-[14px] text-muted-foreground">
          Loading your Instagram connection…
        </div>
      </div>
    );
  }

  if (statusQuery.error || !connection) {
    return (
      <div className="py-6 max-w-4xl mx-auto px-2">
        {Heading}
        <div className="bg-card rounded-2xl border border-border p-6 text-[14px] text-muted-foreground">
          Could not load your Instagram connection.
        </div>
      </div>
    );
  }

  const isConnected = connection.status === InstagramConnectionStatus.CONNECTED;
  const needsReconnect =
    connection.status === InstagramConnectionStatus.EXPIRED ||
    connection.status === InstagramConnectionStatus.REVOKED ||
    connection.status === InstagramConnectionStatus.ERROR;

  return (
    <div className="py-6 max-w-4xl mx-auto px-2 space-y-5">
      {Heading}

      <Section title="Account">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <InstagramStatusPill status={connection.status} />
              {connection.instagramUsername && (
                <span className="text-[14px] font-medium text-foreground">
                  @{connection.instagramUsername}
                </span>
              )}
            </div>
            {isConnected && (
              <p className="text-[12px] text-muted-foreground mt-1">
                Connected {formatInstagramDate(connection.connectedAt)} · token expires{' '}
                {formatInstagramDate(connection.tokenExpiresAt)}
              </p>
            )}
            {needsReconnect && (
              <p className="text-[12px] text-muted-foreground mt-1">
                Your storefront feed is hidden until you reconnect.
              </p>
            )}
            {connection.status === InstagramConnectionStatus.DISCONNECTED && (
              <p className="text-[12px] text-muted-foreground mt-1">
                Connecting opens Instagram, where you approve read-only access to your recent
                posts. We never post on your behalf.
              </p>
            )}
          </div>

          <div className="flex items-center gap-3">
            {(isConnected || needsReconnect) && (
              <Button
                variant="outline"
                size="sm"
                className="h-[40px]"
                onClick={() => setConfirmDisconnect(true)}
                disabled={disconnect.isPending}
              >
                Disconnect
              </Button>
            )}
            <Button
              size="sm"
              className="h-[40px]"
              onClick={() => connect.mutate()}
              disabled={connect.isPending}
              loading={connect.isPending}
            >
              {isConnected || needsReconnect ? 'Reconnect' : 'Connect Instagram'}
            </Button>
          </div>
        </div>
      </Section>

      <Section
        title="Storefront section"
        description="Controls whether the Instagram strip appears on your storefront homepage, and how often we refresh it."
      >
        <div className="space-y-4">
          <Toggle
            label="Show my Instagram posts on the storefront"
            checked={feedEnabled}
            onChange={setFeedEnabled}
            disabled={!isConnected}
            title={isConnected ? undefined : 'Connect an Instagram account first.'}
          />
          <div className="max-w-[260px]">
            <Input
              label="Refresh every (minutes)"
              type="number"
              min={REFRESH_CADENCE_MIN_MINUTES}
              max={REFRESH_CADENCE_MAX_MINUTES}
              value={cadence}
              onChange={(e) => setCadence(e.target.value)}
              disabled={!isConnected}
            />
          </div>
          {!cadenceValid && cadence !== '' && (
            <p className="text-[12px] text-red-600">
              Enter a whole number between {REFRESH_CADENCE_MIN_MINUTES} and{' '}
              {REFRESH_CADENCE_MAX_MINUTES} minutes.
            </p>
          )}
          <p className="text-[12px] text-muted-foreground">
            Last refresh:{' '}
            <InstagramFetchHealth
              lastFetchAt={connection.lastFetchAt}
              lastFetchSucceeded={connection.lastFetchSucceeded}
              lastFetchError={connection.lastFetchError}
            />
          </p>
          <div className="flex justify-end">
            <Button
              size="sm"
              className="h-[40px]"
              onClick={saveSettings}
              disabled={!settingsDirty || !cadenceValid || updateSettings.isPending}
              loading={updateSettings.isPending}
            >
              Save settings
            </Button>
          </div>
        </div>
      </Section>

      <Modal isOpen={confirmDisconnect} onClose={() => setConfirmDisconnect(false)} size="sm">
        <div className="p-6 space-y-4">
          <h2 className="text-[16px] font-semibold text-foreground">Disconnect Instagram?</h2>
          <p className="text-[14px] text-foreground">
            The Instagram section disappears from your storefront immediately and your stored
            access token is cleared. You can reconnect at any time.
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="outline" size="sm" onClick={() => setConfirmDisconnect(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={async () => {
                await disconnect.mutateAsync();
                setConfirmDisconnect(false);
              }}
              disabled={disconnect.isPending}
              loading={disconnect.isPending}
            >
              Disconnect
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
